import { WHATSAPP_CONSTANTS } from '../constants';
import type { WhatsAppBusinessData, FacebookAuthResponse } from '../types/whatsapp';

export class StorageService {
  static getStorageData<T = any>(): T | null {
    try {
      const data = localStorage.getItem(WHATSAPP_CONSTANTS.STORAGE_KEY);
      return data ? JSON.parse(data) : null;
    } catch (error) {
      console.error('Error reading from localStorage:', error);
      return null;
    }
  }

  static setStorageData(data: any): void {
    try {
      localStorage.setItem(WHATSAPP_CONSTANTS.STORAGE_KEY, JSON.stringify(data));
    } catch (error) {
      console.error('Error writing to localStorage:', error);
    }
  }

  static clearStorageData(): void {
    localStorage.removeItem(WHATSAPP_CONSTANTS.STORAGE_KEY);
  }

  static updateWhatsAppBusinessData(data: Partial<WhatsAppBusinessData>, websiteId?: string): void {
    const existingData = this.getStorageData() || {};
    
    const updatedData = {
      ...existingData,
      websiteId: websiteId || existingData.websiteId,
      whatsapp_business_onboarding: {
        ...existingData.whatsapp_business_onboarding,
        ...data,
        timestamp: new Date().toISOString()
      },
      last_updated: new Date().toISOString()
    };
    
    this.setStorageData(updatedData);
  }

  static updateFacebookLoginData(authResponse: FacebookAuthResponse, websiteId?: string): void {
    const existingData = this.getStorageData() || {};
    
    const updatedData = {
      ...existingData,
      websiteId: websiteId || existingData.websiteId,
      facebook_login_callback: {
        authResponse: authResponse.authResponse,
        status: authResponse.status,
        timestamp: new Date().toISOString()
      },
      last_updated: new Date().toISOString()
    };
    
    this.setStorageData(updatedData);
  }

  static updateAccessToken(token: string, websiteId?: string): void {
    const existingData = this.getStorageData() || {};
    
    const updatedData = {
      ...existingData,
      websiteId: websiteId || existingData.websiteId,
      access_token: {
        token,
        obtained_at: new Date().toISOString()
      },
      last_updated: new Date().toISOString()
    };
    
    this.setStorageData(updatedData);
  }

  static getAccessToken(): string | null {
    const data = this.getStorageData();
    return data?.access_token?.token || null;
  }

  static getWhatsAppBusinessData(): WhatsAppBusinessData | null {
    const data = this.getStorageData();
    return data?.whatsapp_business_onboarding || null;
  }

  static getFacebookLoginData(): FacebookAuthResponse | null {
    const data = this.getStorageData();
    return data?.facebook_login_callback || null;
  }

  static validateAndGetStorageData(currentWebsiteId: string): any | null {
    const data = this.getStorageData();
    
    if (!data) {
      return null;
    }
    
    // Check if the stored data belongs to the current website
    if (data.websiteId && data.websiteId !== currentWebsiteId) {
      // Data belongs to a different website, clear it
      this.clearStorageData();
      return null;
    }
    
    // If no websiteId is stored, add the current one
    if (!data.websiteId) {
      const updatedData = {
        ...data,
        websiteId: currentWebsiteId,
        last_updated: new Date().toISOString()
      };
      this.setStorageData(updatedData);
      return updatedData;
    }
    
    return data;
  }
}